import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  Plus, Edit, Trash2, Search, Filter, Copy, Eye, 
  Calendar, Users, Tag, Percent, DollarSign, Gift,
  Clock, AlertCircle, CheckCircle, XCircle, Info, X, Download
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';

const CouponsManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    user_type: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Bulk actions state
  const [selectedCoupons, setSelectedCoupons] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  
  // Product and category search states
  const [applicableProducts, setApplicableProducts] = useState([]);
  const [excludedProducts, setExcludedProducts] = useState([]);
  const [applicableCategories, setApplicableCategories] = useState([]);
  const [excludedCategories, setExcludedCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchingApplicableProducts, setSearchingApplicableProducts] = useState(false);
  const [searchingExcludedProducts, setSearchingExcludedProducts] = useState(false);
  const [searchingApplicableCategories, setSearchingApplicableCategories] = useState(false);
  const [searchingExcludedCategories, setSearchingExcludedCategories] = useState(false);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [showApplicableProductDropdown, setShowApplicableProductDropdown] = useState(false);
  const [showExcludedProductDropdown, setShowExcludedProductDropdown] = useState(false);
  const [showApplicableCategoryDropdown, setShowApplicableCategoryDropdown] = useState(false);
  const [showExcludedCategoryDropdown, setShowExcludedCategoryDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [excludedProductSearchTerm, setExcludedProductSearchTerm] = useState('');
  const [excludedCategorySearchTerm, setExcludedCategorySearchTerm] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    type: 'percentage',
    discount_percentage: 0,
    discount_amount: 0,
    max_discount_amount: 0,
    min_cart_value: 0,
    max_cart_value: 0,
    user_type: 'all',
    max_usage_total: null,
    max_usage_per_user: 1,
    start_date: '',
    end_date: '',
    is_active: true,
    is_public: true,
    priority: 0,
    // Product restrictions
    applicable_products: [],
    excluded_products: [],
    applicable_categories: [],
    excluded_categories: [],
    // Time-based limits
    daily_limit: null,
    weekly_limit: null,
    monthly_limit: null,
    yearly_limit: null,
    // Date/time restrictions
    valid_days: [],
    valid_hours: [],
    // Special conditions
    first_order_only: false,
    first_login_only: false,
    repeat_customer_only: false,
    abandoned_cart_only: false,
    birthday_only: false,
    // Buy 1 Get 1 settings
    bogo_free_product_id: null,
    bogo_quantity: 1,
    // Membership settings
    membership_level: '',
    specific_user_ids: []
  });

  // Fetch coupons
  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        search: searchTerm,
        ...filters
      });

      const response = await api.get(`/coupons?${params}`);
      if (response.data.success) {
        setCoupons(response.data.data.coupons);
        setTotalPages(response.data.data.pagination.pages);
      }
    } catch (error) {
      toast.error('Failed to fetch coupons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, [currentPage, searchTerm, filters]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside all search containers
      const isOutsideSearch = !event.target.closest('.search-container');
      if (isOutsideSearch) {
        setShowApplicableProductDropdown(false);
        setShowExcludedProductDropdown(false);
        setShowApplicableCategoryDropdown(false);
        setShowExcludedCategoryDropdown(false);
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle array input changes (for products, categories, etc.)
  const handleArrayInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: Array.isArray(value) ? value : value.split(',').map(item => item.trim()).filter(item => item)
    }));
  };

  // Handle checkbox array changes (for valid days)
  const handleCheckboxArrayChange = (field, value, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...(prev[field] || []), value]
        : (prev[field] || []).filter(item => item !== value)
    }));
  };

  // Generate random coupon code
  const generateCouponCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    handleInputChange('code', result);
  };

  // Bulk actions functions
  const handleSelectCoupon = (couponId) => {
    setSelectedCoupons(prev => {
      const isSelected = prev.includes(couponId);
      if (isSelected) {
        return prev.filter(id => id !== couponId);
      } else {
        return [...prev, couponId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCoupons([]);
      setSelectAll(false);
    } else {
      setSelectedCoupons(coupons.map(coupon => coupon.id));
      setSelectAll(true);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedCoupons.length) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedCoupons.length} coupon(s)?`)) {
      return;
    }

    try {
      const promises = selectedCoupons.map(id => api.delete(`/coupons/${id}`));
      await Promise.all(promises);
      
      toast.success(`Successfully deleted ${selectedCoupons.length} coupon(s)`);
      setSelectedCoupons([]);
      setSelectAll(false);
      fetchCoupons();
    } catch (error) {
      toast.error('Failed to delete some coupons');
    }
  };

  const handleBulkActivate = async () => {
    if (!selectedCoupons.length) return;

    try {
      const promises = selectedCoupons.map(id => 
        api.put(`/coupons/${id}`, { is_active: true })
      );
      await Promise.all(promises);
      
      toast.success(`Successfully activated ${selectedCoupons.length} coupon(s)`);
      setSelectedCoupons([]);
      setSelectAll(false);
      fetchCoupons();
    } catch (error) {
      toast.error('Failed to activate some coupons');
    }
  };

  const handleBulkDeactivate = async () => {
    if (!selectedCoupons.length) return;

    try {
      const promises = selectedCoupons.map(id => 
        api.put(`/coupons/${id}`, { is_active: false })
      );
      await Promise.all(promises);
      
      toast.success(`Successfully deactivated ${selectedCoupons.length} coupon(s)`);
      setSelectedCoupons([]);
      setSelectAll(false);
      fetchCoupons();
    } catch (error) {
      toast.error('Failed to deactivate some coupons');
    }
  };

  const handleBulkDuplicate = async () => {
    if (!selectedCoupons.length) return;

    try {
      const promises = selectedCoupons.map(async (id) => {
        const coupon = coupons.find(c => c.id === id);
        if (!coupon) return;
        
        const duplicateData = {
          ...coupon,
          code: `${coupon.code}_COPY_${Date.now()}`,
          name: `${coupon.name} (Copy)`,
          is_active: false // Duplicates are inactive by default
        };
        delete duplicateData.id;
        delete duplicateData.created_at;
        delete duplicateData.updated_at;
        
        return api.post('/coupons', duplicateData);
      });
      
      await Promise.all(promises);
      toast.success(`Successfully duplicated ${selectedCoupons.length} coupon(s)`);
      setSelectedCoupons([]);
      setSelectAll(false);
      fetchCoupons();
    } catch (error) {
      toast.error('Failed to duplicate some coupons');
    }
  };

  const handleBulkExport = () => {
    if (!selectedCoupons.length) return;

    const selectedCouponData = coupons.filter(coupon => selectedCoupons.includes(coupon.id));
    
    // Create CSV content
    const headers = ['Code', 'Name', 'Type', 'Discount', 'Usage Count', 'Status', 'Start Date', 'End Date', 'Is Active'];
    const csvContent = [
      headers.join(','),
      ...selectedCouponData.map(coupon => [
        coupon.code,
        `"${coupon.name}"`,
        coupon.type,
        coupon.discount_percentage || coupon.discount_amount,
        coupon.usage_count || 0,
        coupon.is_active ? 'Active' : 'Inactive',
        coupon.start_date || '',
        coupon.end_date || '',
        coupon.is_active ? 'Yes' : 'No'
      ].join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `coupons_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast.success(`Exported ${selectedCoupons.length} coupon(s) to CSV`);
  };

  // Update showBulkActions when selectedCoupons changes
  useEffect(() => {
    setShowBulkActions(selectedCoupons.length > 0);
  }, [selectedCoupons]);

  // Search products for applicable products
  const searchApplicableProducts = async (searchTerm) => {
    if (searchTerm.length < 2) {
      setApplicableProducts([]);
      return;
    }
    
    try {
      setSearchingApplicableProducts(true);
      const response = await api.get(`/products?search=${searchTerm}&limit=10&status=active`);
      if (response.data.success) {
        setApplicableProducts(response.data.data.products || []);
      }
    } catch (error) {
      console.error('Error searching applicable products:', error);
      setApplicableProducts([]);
    } finally {
      setSearchingApplicableProducts(false);
    }
  };

  // Search products for excluded products
  const searchExcludedProducts = async (searchTerm) => {
    if (searchTerm.length < 2) {
      setExcludedProducts([]);
      return;
    }
    
    try {
      setSearchingExcludedProducts(true);
      const response = await api.get(`/products?search=${searchTerm}&limit=10&status=active`);
      if (response.data.success) {
        setExcludedProducts(response.data.data.products || []);
      }
    } catch (error) {
      console.error('Error searching excluded products:', error);
      setExcludedProducts([]);
    } finally {
      setSearchingExcludedProducts(false);
    }
  };

  // Search categories for applicable categories
  const searchApplicableCategories = async (searchTerm) => {
    if (searchTerm.length < 2) {
      setApplicableCategories([]);
      return;
    }
    
    try {
      setSearchingApplicableCategories(true);
      const response = await api.get(`/product-categories?search=${searchTerm}&status=active`);
      if (response.data.success) {
        setApplicableCategories(response.data.data.categories || []);
      }
    } catch (error) {
      console.error('Error searching applicable categories:', error);
      setApplicableCategories([]);
    } finally {
      setSearchingApplicableCategories(false);
    }
  };

  // Search categories for excluded categories
  const searchExcludedCategories = async (searchTerm) => {
    if (searchTerm.length < 2) {
      setExcludedCategories([]);
      return;
    }
    
    try {
      setSearchingExcludedCategories(true);
      const response = await api.get(`/product-categories?search=${searchTerm}&status=active`);
      if (response.data.success) {
        setExcludedCategories(response.data.data.categories || []);
      }
    } catch (error) {
      console.error('Error searching excluded categories:', error);
      setExcludedCategories([]);
    } finally {
      setSearchingExcludedCategories(false);
    }
  };

  // Search users
  const searchUsers = async (searchTerm) => {
    if (searchTerm.length < 2) {
      setUsers([]);
      return;
    }
    
    try {
      setSearchingUsers(true);
      const response = await api.get(`/users?search=${searchTerm}&limit=10&status=active`);
      if (response.data.success) {
        setUsers(response.data.data.users || []);
      }
    } catch (error) {
      console.error('Error searching users:', error);
      setUsers([]);
    } finally {
      setSearchingUsers(false);
    }
  };

  // Handle product selection
  const handleProductSelect = (product, field) => {
    const currentProducts = formData[field] || [];
    const productData = { id: product.id, name: product.name };
    
    // Check if product is already selected
    const isAlreadySelected = currentProducts.some(p => p.id === product.id);
    if (!isAlreadySelected) {
      handleInputChange(field, [...currentProducts, productData]);
    }
    
    // Clear the appropriate search term and close dropdown based on field
    if (field === 'applicable_products') {
      setProductSearchTerm('');
      setShowApplicableProductDropdown(false);
    } else if (field === 'excluded_products') {
      setExcludedProductSearchTerm('');
      setShowExcludedProductDropdown(false);
    }
  };

  // Handle category selection
  const handleCategorySelect = (category, field) => {
    const currentCategories = formData[field] || [];
    const categoryData = { id: category.id, name: category.name };
    
    // Check if category is already selected
    const isAlreadySelected = currentCategories.some(c => c.id === category.id);
    if (!isAlreadySelected) {
      handleInputChange(field, [...currentCategories, categoryData]);
    }
    
    // Clear the appropriate search term and close dropdown based on field
    if (field === 'applicable_categories') {
      setCategorySearchTerm('');
      setShowApplicableCategoryDropdown(false);
    } else if (field === 'excluded_categories') {
      setExcludedCategorySearchTerm('');
      setShowExcludedCategoryDropdown(false);
    }
  };

  // Handle user selection
  const handleUserSelect = (user) => {
    const currentUsers = formData.specific_user_ids || [];
    const userData = { id: user.id, name: user.name };
    
    // Check if user is already selected
    const isAlreadySelected = currentUsers.some(u => u.id === user.id);
    if (!isAlreadySelected) {
      handleInputChange('specific_user_ids', [...currentUsers, userData]);
    }
    
    setUserSearchTerm('');
    setShowUserDropdown(false);
  };

  // Remove selected product/category
  const removeSelected = (itemId, field) => {
    const currentItems = formData[field] || [];
    handleInputChange(field, currentItems.filter(item => item.id !== itemId));
  };

  // Fetch product names by IDs
  const fetchProductNames = async (productIds) => {
    if (!productIds || productIds.length === 0) return [];
    
    try {
      const response = await api.get(`/products?ids=${productIds.join(',')}`);
      if (response.data.success) {
        return response.data.data.products.map(p => ({ id: p.id, name: p.name }));
      }
    } catch (error) {
      console.error('Error fetching product names:', error);
    }
    return [];
  };

  // Fetch category names by IDs
  const fetchCategoryNames = async (categoryIds) => {
    if (!categoryIds || categoryIds.length === 0) return [];
    
    try {
      const response = await api.get(`/product-categories?ids=${categoryIds.join(',')}`);
      if (response.data.success) {
        return response.data.data.categories.map(c => ({ id: c.id, name: c.name }));
      }
    } catch (error) {
      console.error('Error fetching category names:', error);
    }
    return [];
  };

  // Fetch user names by IDs
  const fetchUserNames = async (userIds) => {
    if (!userIds || userIds.length === 0) return [];
    
    try {
      const response = await api.get(`/users?ids=${userIds.join(',')}`);
      if (response.data.success) {
        return response.data.data.users.map(u => ({ id: u.id, name: u.name }));
      }
    } catch (error) {
      console.error('Error fetching user names:', error);
    }
    return [];
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      description: '',
      type: 'percentage',
      discount_percentage: 0,
      discount_amount: 0,
      max_discount_amount: 0,
      min_cart_value: 0,
      max_cart_value: 0,
      user_type: 'all',
      max_usage_total: null,
      max_usage_per_user: 1,
      start_date: '',
      end_date: '',
      is_active: true,
      is_public: true,
      priority: 0,
      // Product restrictions (now objects with id and name)
      applicable_products: [],
      excluded_products: [],
      applicable_categories: [],
      excluded_categories: [],
      // Time-based limits
      daily_limit: null,
      weekly_limit: null,
      monthly_limit: null,
      yearly_limit: null,
      // Date/time restrictions
      valid_days: [],
      valid_hours: [],
      // Special conditions
      first_order_only: false,
      first_login_only: false,
      repeat_customer_only: false,
      abandoned_cart_only: false,
      birthday_only: false,
      // Buy 1 Get 1 settings
      bogo_free_product_id: null,
      bogo_quantity: 1,
      // Membership settings
      membership_level: '',
      specific_user_ids: []
    });
    setEditingCoupon(null);
  };

  // Open modal for creating/editing
  const openModal = async (coupon = null) => {
    if (coupon) {
      // Parse JSON fields and convert to proper format
      const parsedCoupon = {
        ...coupon,
        start_date: coupon.start_date ? coupon.start_date.slice(0, 16) : '',
        end_date: coupon.end_date ? coupon.end_date.slice(0, 16) : '',
        applicable_products: Array.isArray(coupon.applicable_products) ? coupon.applicable_products : [],
        excluded_products: Array.isArray(coupon.excluded_products) ? coupon.excluded_products : [],
        applicable_categories: Array.isArray(coupon.applicable_categories) ? coupon.applicable_categories : [],
        excluded_categories: Array.isArray(coupon.excluded_categories) ? coupon.excluded_categories : []
      };
      
      // Convert IDs to objects with names (for display purposes)
      const productIds = [...(parsedCoupon.applicable_products || []), ...(parsedCoupon.excluded_products || [])];
      const categoryIds = [...(parsedCoupon.applicable_categories || []), ...(parsedCoupon.excluded_categories || [])];
      const userIds = parsedCoupon.specific_user_ids || [];
      
      // Fetch names for products, categories, and users
      const [productNames, categoryNames, userNames] = await Promise.all([
        fetchProductNames(productIds),
        fetchCategoryNames(categoryIds),
        fetchUserNames(userIds)
      ]);
      
      // Create lookup maps
      const productMap = new Map(productNames.map(p => [p.id, p.name]));
      const categoryMap = new Map(categoryNames.map(c => [c.id, c.name]));
      const userMap = new Map(userNames.map(u => [u.id, u.name]));
      
      // Convert IDs to objects with names
      parsedCoupon.applicable_products = (parsedCoupon.applicable_products || []).map(id => ({
        id: typeof id === 'object' ? id.id : id,
        name: productMap.get(typeof id === 'object' ? id.id : id) || `Product ${id}`
      }));
      
      parsedCoupon.excluded_products = (parsedCoupon.excluded_products || []).map(id => ({
        id: typeof id === 'object' ? id.id : id,
        name: productMap.get(typeof id === 'object' ? id.id : id) || `Product ${id}`
      }));
      
      parsedCoupon.applicable_categories = (parsedCoupon.applicable_categories || []).map(id => ({
        id: typeof id === 'object' ? id.id : id,
        name: categoryMap.get(typeof id === 'object' ? id.id : id) || `Category ${id}`
      }));
      
      parsedCoupon.excluded_categories = (parsedCoupon.excluded_categories || []).map(id => ({
        id: typeof id === 'object' ? id.id : id,
        name: categoryMap.get(typeof id === 'object' ? id.id : id) || `Category ${id}`
      }));
      
      parsedCoupon.specific_user_ids = (parsedCoupon.specific_user_ids || []).map(id => ({
        id: typeof id === 'object' ? id.id : id,
        name: userMap.get(typeof id === 'object' ? id.id : id) || `User ${id}`
      }));
      
      setFormData(parsedCoupon);
      setEditingCoupon(coupon);
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  // Create/Update coupon
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Form submitted with data:', formData);
    
    try {
      const submitData = {
        ...formData,
        code: formData.code.toUpperCase(),
        discount_percentage: parseFloat(formData.discount_percentage) || 0,
        discount_amount: parseFloat(formData.discount_amount) || 0,
        max_discount_amount: parseFloat(formData.max_discount_amount) || 0,
        min_cart_value: parseFloat(formData.min_cart_value) || 0,
        max_cart_value: parseFloat(formData.max_cart_value) || 0,
        max_usage_total: formData.max_usage_total ? parseInt(formData.max_usage_total) : null,
        max_usage_per_user: parseInt(formData.max_usage_per_user) || 1,
        priority: parseInt(formData.priority) || 0
      };

      console.log('Processed submit data:', submitData);

      if (editingCoupon) {
        console.log('Updating coupon:', editingCoupon.id);
        await api.put(`/coupons/${editingCoupon.id}`, submitData);
        toast.success('Coupon updated successfully');
      } else {
        console.log('Creating new coupon');
        const response = await api.post('/coupons', submitData);
        console.log('API response:', response);
        console.log('Response data:', response.data);
        console.log('Response status:', response.status);
        toast.success('Coupon created successfully');
      }

      closeModal();
      fetchCoupons();
    } catch (error) {
      console.error('Error creating/updating coupon:', error);
      toast.error(error.message || 'Failed to save coupon');
    }
  };

  // Delete coupon
  const handleDelete = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) {
      return;
    }

    try {
      await api.delete(`/coupons/${couponId}`);
      toast.success('Coupon deleted successfully');
      fetchCoupons();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  // Copy coupon code
  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Coupon code copied to clipboard');
  };

  // Get coupon type icon
  const getTypeIcon = (type) => {
    switch (type) {
      case 'percentage':
        return <Percent className="w-4 h-4" />;
      case 'fixed_amount':
        return <DollarSign className="w-4 h-4" />;
      case 'buy_one_get_one':
        return <Gift className="w-4 h-4" />;
      case 'free_shipping':
        return <Tag className="w-4 h-4" />;
      default:
        return <Tag className="w-4 h-4" />;
    }
  };

  // Get status badge
  const getStatusBadge = (coupon) => {
    const now = new Date();
    const startDate = coupon.start_date ? new Date(coupon.start_date) : null;
    const endDate = coupon.end_date ? new Date(coupon.end_date) : null;

    if (!coupon.is_active) {
      return <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">Inactive</span>;
    }

    if (startDate && startDate > now) {
      return <span className="px-2 py-1 text-xs bg-blue-100 text-blue-600 rounded-full">Pending</span>;
    }

    if (endDate && endDate < now) {
      return <span className="px-2 py-1 text-xs bg-red-100 text-red-600 rounded-full">Expired</span>;
    }

    return <span className="px-2 py-1 text-xs bg-green-100 text-green-600 rounded-full">Active</span>;
  };

  // Get discount display
  const getDiscountDisplay = (coupon) => {
    switch (coupon.type) {
      case 'percentage':
        return `${coupon.discount_percentage}% off`;
      case 'fixed_amount':
        return `₹${coupon.discount_amount} off`;
      case 'buy_one_get_one':
        return 'Buy 1 Get 1';
      case 'free_shipping':
        return 'Free Shipping';
      default:
        return 'Discount';
    }
  };

  return (
    <div className="p-4 sm:p-6 bg-white dark:bg-gray-900 rounded-lg shadow-sm">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Coupon Management</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage discount coupons and promotional codes</p>
        </div>
        <button
                          onClick={async () => await openModal()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Coupon
        </button>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search coupons..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
        
        <div className="flex gap-2">
          <select
            value={filters.type}
            onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="">All Types</option>
            <option value="percentage">Percentage</option>
            <option value="fixed_amount">Fixed Amount</option>
            <option value="buy_one_get_one">Buy 1 Get 1</option>
            <option value="free_shipping">Free Shipping</option>
          </select>
          
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          
          <select
            value={filters.user_type}
            onChange={(e) => setFilters(prev => ({ ...prev, user_type: e.target.value }))}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="">All Users</option>
            <option value="all">All Users</option>
            <option value="new_users">New Users</option>
            <option value="existing_users">Existing Users</option>
            <option value="membership_users">Membership Users</option>
            <option value="specific_users">Specific Users</option>
          </select>
        </div>
      </div>

      {/* Bulk Actions Toolbar */}
      {showBulkActions && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                {selectedCoupons.length} coupon(s) selected
              </span>
              <button
                onClick={() => {
                  setSelectedCoupons([]);
                  setSelectAll(false);
                }}
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
              >
                Clear selection
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleBulkActivate}
                className="flex items-center gap-2 px-3 py-1.5 bg-green-500 text-white text-sm rounded-md hover:bg-green-600 transition-colors"
              >
                <CheckCircle className="w-4 h-4" />
                Activate
              </button>
              <button
                onClick={handleBulkDeactivate}
                className="flex items-center gap-2 px-3 py-1.5 bg-yellow-500 text-white text-sm rounded-md hover:bg-yellow-600 transition-colors"
              >
                <XCircle className="w-4 h-4" />
                Deactivate
              </button>
              <button
                onClick={handleBulkDuplicate}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600 transition-colors"
              >
                <Copy className="w-4 h-4" />
                Duplicate
              </button>
              <button
                onClick={handleBulkExport}
                className="flex items-center gap-2 px-3 py-1.5 bg-purple-500 text-white text-sm rounded-md hover:bg-purple-600 transition-colors"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
              <button
                onClick={handleBulkDelete}
                className="flex items-center gap-2 px-3 py-1.5 bg-red-500 text-white text-sm rounded-md hover:bg-red-600 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Coupons Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-800 dark:text-gray-400">
            <tr>
              <th className="px-6 py-3">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <span className="text-xs">All</span>
                </div>
              </th>
              <th className="px-6 py-3">Code</th>
              <th className="px-6 py-3">Name</th>
              <th className="px-6 py-3">Type</th>
              <th className="px-6 py-3">Discount</th>
              <th className="px-6 py-3">Usage</th>
              <th className="px-6 py-3">Status</th>
              <th className="px-6 py-3">Valid Until</th>
              <th className="px-6 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="px-6 py-4 text-center">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-blue-600"></div>
                    <span className="ml-2">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : coupons.length === 0 ? (
              <tr>
                <td colSpan="9" className="px-6 py-4 text-center text-gray-500">
                  No coupons found
                </td>
              </tr>
            ) : (
              coupons.map((coupon) => (
                <tr key={coupon.id} className="bg-white border-b dark:bg-gray-900 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedCoupons.includes(coupon.id)}
                      onChange={() => handleSelectCoupon(coupon.id)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-medium text-blue-600">{coupon.code}</span>
                      <button
                        onClick={() => copyCode(coupon.code)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{coupon.name}</div>
                      {coupon.description && (
                        <div className="text-xs text-gray-500 truncate max-w-xs">{coupon.description}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {getTypeIcon(coupon.type)}
                      <span className="capitalize">{coupon.type.replace('_', ' ')}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-medium text-green-600">{getDiscountDisplay(coupon)}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-xs">
                      <div>{coupon.usage_count || 0} uses</div>
                      <div className="text-gray-500">{coupon.unique_users_used || 0} users</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(coupon)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-xs">
                      {coupon.end_date ? (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(coupon.end_date).toLocaleDateString()}
                        </div>
                      ) : (
                        <span className="text-gray-500">No expiry</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={async () => await openModal(coupon)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(coupon.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 py-2">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-visible flex flex-col relative z-10">
            {/* Modal Header */}
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}
                </h2>
                <button
                  onClick={closeModal}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <form onSubmit={handleSubmit} className="p-4 sm:p-6 overflow-y-auto overflow-x-visible flex-1 space-y-6 relative z-0">
              {/* Basic Information */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:p-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Coupon Code *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="WELCOME10"
                      required
                    />
                    <button
                      type="button"
                      onClick={generateCouponCode}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                    >
                      Generate
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Coupon Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Welcome Discount"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  rows="3"
                  placeholder="Description of the coupon..."
                />
              </div>

              {/* Discount Settings */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:p-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Type *
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    required
                  >
                    <option value="percentage">Percentage Discount</option>
                    <option value="fixed_amount">Fixed Amount</option>
                    <option value="buy_one_get_one">Buy 1 Get 1</option>
                    <option value="free_shipping">Free Shipping</option>
                  </select>
                </div>

                {formData.type === 'percentage' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Discount Percentage
                      </label>
                      <input
                        type="number"
                        value={formData.discount_percentage}
                        onChange={(e) => handleInputChange('discount_percentage', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        min="0"
                        max="100"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Max Discount Amount
                      </label>
                      <input
                        type="number"
                        value={formData.max_discount_amount}
                        onChange={(e) => handleInputChange('max_discount_amount', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </>
                )}

                {formData.type === 'fixed_amount' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Discount Amount
                    </label>
                    <input
                      type="number"
                      value={formData.discount_amount}
                      onChange={(e) => handleInputChange('discount_amount', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      min="0"
                      step="0.01"
                    />
                  </div>
                )}
              </div>

              {/* Cart Value Restrictions */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:p-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Minimum Cart Value
                  </label>
                  <input
                    type="number"
                    value={formData.min_cart_value}
                    onChange={(e) => handleInputChange('min_cart_value', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Maximum Cart Value
                  </label>
                  <input
                    type="number"
                    value={formData.max_cart_value}
                    onChange={(e) => handleInputChange('max_cart_value', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Usage Limits */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:p-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    User Type
                  </label>
                  <select
                    value={formData.user_type}
                    onChange={(e) => handleInputChange('user_type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="all">All Users</option>
                    <option value="new_users">New Users Only</option>
                    <option value="existing_users">Existing Users Only</option>
                    <option value="membership_users">Membership Users</option>
                    <option value="specific_users">Specific Users</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Max Total Usage
                  </label>
                  <input
                    type="number"
                    value={formData.max_usage_total || ''}
                    onChange={(e) => handleInputChange('max_usage_total', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    min="0"
                    placeholder="Unlimited"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Max Per User
                  </label>
                  <input
                    type="number"
                    value={formData.max_usage_per_user}
                    onChange={(e) => handleInputChange('max_usage_per_user', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    min="1"
                  />
                </div>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:p-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Start Date
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={(e) => handleInputChange('start_date', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    End Date
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => handleInputChange('end_date', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              {/* Time-based Usage Limits */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:p-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Daily Limit
                  </label>
                  <input
                    type="number"
                    value={formData.daily_limit || ''}
                    onChange={(e) => handleInputChange('daily_limit', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    min="0"
                    placeholder="Unlimited"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Weekly Limit
                  </label>
                  <input
                    type="number"
                    value={formData.weekly_limit || ''}
                    onChange={(e) => handleInputChange('weekly_limit', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    min="0"
                    placeholder="Unlimited"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Monthly Limit
                  </label>
                  <input
                    type="number"
                    value={formData.monthly_limit || ''}
                    onChange={(e) => handleInputChange('monthly_limit', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    min="0"
                    placeholder="Unlimited"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Yearly Limit
                  </label>
                  <input
                    type="number"
                    value={formData.yearly_limit || ''}
                    onChange={(e) => handleInputChange('yearly_limit', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    min="0"
                    placeholder="Unlimited"
                  />
                </div>
              </div>

              {/* Product Restrictions */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:p-6">
                <div className="relative search-container z-20">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Applicable Products
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={productSearchTerm}
                      onChange={(e) => {
                        setProductSearchTerm(e.target.value);
                        if (e.target.value.length >= 2) {
                          searchApplicableProducts(e.target.value);
                          setShowApplicableProductDropdown(true);
                        } else {
                          setShowApplicableProductDropdown(false);
                        }
                      }}
                      onFocus={() => {
                        if (productSearchTerm.length >= 2) {
                          setShowApplicableProductDropdown(true);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                    <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                  </div>
                  
                  {/* Selected Products */}
                  {formData.applicable_products.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {formData.applicable_products.map((product) => (
                        <div key={product.id} className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded text-sm">
                          <span className="text-blue-700 dark:text-blue-300">{product.name}</span>
                          <button
                            type="button"
                            onClick={() => removeSelected(product.id, 'applicable_products')}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Product Dropdown */}
                  {showApplicableProductDropdown && (
                    <div className="fixed bg-white dark:bg-gray-900 border-2 border-blue-200 dark:border-blue-700 rounded-lg shadow-2xl max-h-60 overflow-y-auto backdrop-blur-sm ring-4 ring-white/50 dark:ring-gray-900/50"
                         style={{
                           top: '50%',
                           left: '50%',
                           transform: 'translate(-50%, -50%)',
                           width: '400px',
                           maxWidth: '90vw',
                           zIndex: 999999
                         }}>
                      {searchingApplicableProducts ? (
                        <div className="p-3 text-center text-gray-500 dark:text-gray-400">Searching products...</div>
                      ) : applicableProducts.length > 0 ? (
                        applicableProducts.map((product) => (
                          <div
                            key={product.id}
                            onClick={() => handleProductSelect(product, 'applicable_products')}
                            className="p-3 bg-gray-50 dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-0"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">{product.name}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{product.category}</p>
                              </div>
                              <div className="text-right">
                                <span className="text-xs px-2 py-1 rounded-full bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                                  ₹{product.regular_license_price || 0}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-3 text-center text-gray-500 dark:text-gray-400">No products found</div>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="relative search-container z-20">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Excluded Products
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={excludedProductSearchTerm}
                      onChange={(e) => {
                        setExcludedProductSearchTerm(e.target.value);
                        if (e.target.value.length >= 2) {
                          searchExcludedProducts(e.target.value);
                          setShowExcludedProductDropdown(true);
                        } else {
                          setShowExcludedProductDropdown(false);
                        }
                      }}
                      onFocus={() => {
                        if (excludedProductSearchTerm.length >= 2) {
                          setShowExcludedProductDropdown(true);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                    <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                  </div>
                  
                  {/* Selected Products */}
                  {formData.excluded_products.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {formData.excluded_products.map((product) => (
                        <div key={product.id} className="flex items-center justify-between bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded text-sm">
                          <span className="text-red-700 dark:text-red-300">{product.name}</span>
                          <button
                            type="button"
                            onClick={() => removeSelected(product.id, 'excluded_products')}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Product Dropdown */}
                  {showExcludedProductDropdown && (
                    <div className="fixed bg-white dark:bg-gray-900 border-2 border-red-200 dark:border-red-700 rounded-lg shadow-2xl max-h-60 overflow-y-auto backdrop-blur-sm ring-4 ring-white/50 dark:ring-gray-900/50"
                         style={{
                           top: '50%',
                           left: '50%',
                           transform: 'translate(-50%, -50%)',
                           width: '400px',
                           maxWidth: '90vw',
                           zIndex: 999999
                         }}>
                      {searchingExcludedProducts ? (
                        <div className="p-3 text-center text-gray-500 dark:text-gray-400">Searching products...</div>
                      ) : excludedProducts.length > 0 ? (
                        excludedProducts.map((product) => (
                          <div
                            key={product.id}
                            onClick={() => handleProductSelect(product, 'excluded_products')}
                            className="p-3 bg-gray-50 dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/30 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-0"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">{product.name}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{product.category}</p>
                              </div>
                              <div className="text-right">
                                <span className="text-xs px-2 py-1 rounded-full bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                                  ₹{product.regular_license_price || 0}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-3 text-center text-gray-500 dark:text-gray-400">No products found</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Category Restrictions */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:p-6">
                <div className="relative search-container z-20">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Applicable Categories
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search categories..."
                      value={categorySearchTerm}
                      onChange={(e) => {
                        setCategorySearchTerm(e.target.value);
                        if (e.target.value.length >= 2) {
                          searchApplicableCategories(e.target.value);
                          setShowApplicableCategoryDropdown(true);
                        } else {
                          setShowApplicableCategoryDropdown(false);
                        }
                      }}
                      onFocus={() => {
                        if (categorySearchTerm.length >= 2) {
                          setShowApplicableCategoryDropdown(true);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                    <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                  </div>
                  
                  {/* Selected Categories */}
                  {formData.applicable_categories.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {formData.applicable_categories.map((category) => (
                        <div key={category.id} className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded text-sm">
                          <span className="text-blue-700 dark:text-blue-300">{category.name}</span>
                          <button
                            type="button"
                            onClick={() => removeSelected(category.id, 'applicable_categories')}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Category Dropdown */}
                  {showApplicableCategoryDropdown && (
                    <div className="fixed bg-white dark:bg-gray-900 border-2 border-green-200 dark:border-green-700 rounded-lg shadow-2xl max-h-60 overflow-y-auto backdrop-blur-sm ring-4 ring-white/50 dark:ring-gray-900/50"
                         style={{
                           top: '50%',
                           left: '50%',
                           transform: 'translate(-50%, -50%)',
                           width: '400px',
                           maxWidth: '90vw',
                           zIndex: 999999
                         }}>
                      {searchingApplicableCategories ? (
                        <div className="p-3 text-center text-gray-500 dark:text-gray-400">Searching categories...</div>
                      ) : applicableCategories.length > 0 ? (
                        applicableCategories.map((category) => (
                          <div
                            key={category.id}
                            onClick={() => handleCategorySelect(category, 'applicable_categories')}
                            className="p-3 bg-gray-50 dark:bg-gray-800 hover:bg-green-50 dark:hover:bg-green-900/30 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-0"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">{category.name}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{category.description}</p>
                              </div>
                              <div className="text-right">
                                <span className="text-xs px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                                  {category.product_count || 0} products
                                </span>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-3 text-center text-gray-500 dark:text-gray-400">No categories found</div>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="relative search-container z-20">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Excluded Categories
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search categories..."
                      value={excludedCategorySearchTerm}
                      onChange={(e) => {
                        setExcludedCategorySearchTerm(e.target.value);
                        if (e.target.value.length >= 2) {
                          searchExcludedCategories(e.target.value);
                          setShowExcludedCategoryDropdown(true);
                        } else {
                          setShowExcludedCategoryDropdown(false);
                        }
                      }}
                      onFocus={() => {
                        if (excludedCategorySearchTerm.length >= 2) {
                          setShowExcludedCategoryDropdown(true);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                    <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                  </div>
                  
                  {/* Selected Categories */}
                  {formData.excluded_categories.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {formData.excluded_categories.map((category) => (
                        <div key={category.id} className="flex items-center justify-between bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded text-sm">
                          <span className="text-red-700 dark:text-red-300">{category.name}</span>
                          <button
                            type="button"
                            onClick={() => removeSelected(category.id, 'excluded_categories')}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Category Dropdown */}
                  {showExcludedCategoryDropdown && (
                    <div className="fixed bg-white dark:bg-gray-900 border-2 border-orange-200 dark:border-orange-700 rounded-lg shadow-2xl max-h-60 overflow-y-auto backdrop-blur-sm ring-4 ring-white/50 dark:ring-gray-900/50"
                         style={{
                           top: '50%',
                           left: '50%',
                           transform: 'translate(-50%, -50%)',
                           width: '400px',
                           maxWidth: '90vw',
                           zIndex: 999999
                         }}>
                      {searchingExcludedCategories ? (
                        <div className="p-3 text-center text-gray-500 dark:text-gray-400">Searching categories...</div>
                      ) : excludedCategories.length > 0 ? (
                        excludedCategories.map((category) => (
                          <div
                            key={category.id}
                            onClick={() => handleCategorySelect(category, 'excluded_categories')}
                            className="p-3 bg-gray-50 dark:bg-gray-800 hover:bg-orange-50 dark:hover:bg-orange-900/30 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-0"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">{category.name}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{category.description}</p>
                              </div>
                              <div className="text-right">
                                <span className="text-xs px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                                  {category.product_count || 0} products
                                </span>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-3 text-center text-gray-500 dark:text-gray-400">No categories found</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Valid Days */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Valid Days
                </label>
                <div className="grid grid-cols-7 gap-2">
                  {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => (
                    <div key={day} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`day_${index}`}
                        checked={formData.valid_days.includes(index.toString())}
                        onChange={(e) => handleCheckboxArrayChange('valid_days', index.toString(), e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor={`day_${index}`} className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        {day.slice(0, 3)}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Valid Hours */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Valid Hours (24-hour format, comma-separated ranges)
                </label>
                <input
                  type="text"
                  value={formData.valid_hours.join(', ')}
                  onChange={(e) => handleArrayInputChange('valid_hours', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="9-17, 20-23"
                />
              </div>

              {/* Special Conditions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Special Conditions
                </label>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="first_order_only"
                      checked={formData.first_order_only}
                      onChange={(e) => handleInputChange('first_order_only', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="first_order_only" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      First Order Only
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="first_login_only"
                      checked={formData.first_login_only}
                      onChange={(e) => handleInputChange('first_login_only', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="first_login_only" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      First Login Only
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="repeat_customer_only"
                      checked={formData.repeat_customer_only}
                      onChange={(e) => handleInputChange('repeat_customer_only', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="repeat_customer_only" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      Repeat Customer Only
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="abandoned_cart_only"
                      checked={formData.abandoned_cart_only}
                      onChange={(e) => handleInputChange('abandoned_cart_only', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="abandoned_cart_only" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      Abandoned Cart Only
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="birthday_only"
                      checked={formData.birthday_only}
                      onChange={(e) => handleInputChange('birthday_only', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="birthday_only" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      Birthday Only
                    </label>
                  </div>
                </div>
              </div>

              {/* Buy 1 Get 1 Settings */}
              {formData.type === 'buy_one_get_one' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:p-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Free Product ID
                    </label>
                    <input
                      type="number"
                      value={formData.bogo_free_product_id || ''}
                      onChange={(e) => handleInputChange('bogo_free_product_id', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      BOGO Quantity
                    </label>
                    <input
                      type="number"
                      value={formData.bogo_quantity}
                      onChange={(e) => handleInputChange('bogo_quantity', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      min="1"
                    />
                  </div>
                </div>
              )}

              {/* Membership Settings */}
              {formData.user_type === 'membership_users' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Membership Level
                  </label>
                  <input
                    type="text"
                    value={formData.membership_level}
                    onChange={(e) => handleInputChange('membership_level', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="premium, gold, silver"
                  />
                </div>
              )}

              {/* Specific Users */}
              {formData.user_type === 'specific_users' && (
                <div className="relative search-container z-20">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Specific Users
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={userSearchTerm}
                      onChange={(e) => {
                        setUserSearchTerm(e.target.value);
                        if (e.target.value.length >= 2) {
                          searchUsers(e.target.value);
                          setShowUserDropdown(true);
                        } else {
                          setShowUserDropdown(false);
                        }
                      }}
                      onFocus={() => {
                        if (userSearchTerm.length >= 2) {
                          setShowUserDropdown(true);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                    <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                  </div>
                  
                  {/* Selected Users */}
                  {formData.specific_user_ids.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {formData.specific_user_ids.map((user) => (
                        <div key={user.id} className="flex items-center justify-between bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded text-sm">
                          <span className="text-purple-700 dark:text-purple-300">{user.name}</span>
                          <button
                            type="button"
                            onClick={() => removeSelected(user.id, 'specific_user_ids')}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* User Dropdown */}
                  {showUserDropdown && (
                    <div className="fixed bg-white dark:bg-gray-900 border-2 border-purple-200 dark:border-purple-700 rounded-lg shadow-2xl max-h-60 overflow-y-auto backdrop-blur-sm ring-4 ring-white/50 dark:ring-gray-900/50"
                         style={{
                           top: '50%',
                           left: '50%',
                           transform: 'translate(-50%, -50%)',
                           width: '400px',
                           maxWidth: '90vw',
                           zIndex: 999999
                         }}>
                      {searchingUsers ? (
                        <div className="p-3 text-center text-gray-500 dark:text-gray-400">Searching users...</div>
                      ) : users.length > 0 ? (
                        users.map((user) => (
                          <div
                            key={user.id}
                            onClick={() => handleUserSelect(user)}
                            className="p-3 bg-gray-50 dark:bg-gray-800 hover:bg-purple-50 dark:hover:bg-purple-900/30 cursor-pointer border-b border-gray-200 dark:border-gray-600 last:border-0"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">{user.name}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{user.email}</p>
                              </div>
                              <div className="text-right">
                                <span className="text-xs px-2 py-1 rounded-full bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                                  ID: {user.id}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-3 text-center text-gray-500 dark:text-gray-400">No users found</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Settings */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:p-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="is_active" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Active
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_public"
                    checked={formData.is_public}
                    onChange={(e) => handleInputChange('is_public', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="is_public" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Public
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Priority
                  </label>
                  <input
                    type="number"
                    value={formData.priority}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    min="0"
                  />
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  onClick={() => console.log('Submit button clicked')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingCoupon ? 'Update Coupon' : 'Create Coupon'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CouponsManagement;
